import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { track } from '@vercel/analytics';
import { usePDIContext } from '../context/PDIContext';
import { CHECKLIST_SECTIONS, getIssueItems } from '../data/checklistData';
import { DELIVERY_BLOCKERS } from '../components/DeliveryBlockerBanner';
import { useUnlock } from '../hooks/useUnlock';
import { usePageMeta } from '../hooks/usePageMeta';
import { UNLOCK_PRICE_RUPEES } from '../utils/unlock';
import { SITE_URL } from '../utils/pdfCore';
import { buildSharePayload, saveSharedReport } from '../utils/shareReport';
import ResetConfirmModal from '../components/ResetConfirmModal';
import PDFExport from '../components/PDFExport';
import PhotoLightbox from '../components/PhotoLightbox';
import PrintableChecklist from '../components/PrintableChecklist';
import PaywallModal from '../components/PaywallModal';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  FileText,
  AlertOctagon,
  Wrench,
  ChevronRight,
  Trash2,
  Camera,
  MessageSquare,
  FileDown,
  Share2,
  ChevronDown,
  ChevronUp,
  Trophy,
  Target,
  Sparkles,
  ZoomIn,
  Printer,
  ClipboardList,
  Lock,
  Scale,
  Link2,
  Copy,
  Check,
  Loader2,
} from 'lucide-react';

const SummaryPage = () => {
  const navigate = useNavigate();
  const { 
    pdiScore, 
    itemStatuses, 
    notes, 
    resolvedItems,
    markAsResolved,
    photos,
    sectionProgress,
    clearAllData,
    carProfile,
    showToast,
  } = usePDIContext();
  const unlocked = useUnlock();

  usePageMeta({
    title: 'PDI Summary & Report',
    description: 'Review your Pre-Delivery Inspection results, PDI score, and critical issues. Download a professional PDF report or dealer negotiation scripts.',
    path: '/summary',
  });

  const [showResetModal, setShowResetModal] = useState(false);
  const [showPDFExport, setShowPDFExport] = useState(false);
  const [showPrintableChecklist, setShowPrintableChecklist] = useState(false);
  const [showPaywall, setShowPaywall] = useState(null); // null | 'report' | 'printable' | 'share-link'
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [lightboxData, setLightboxData] = useState(null); // { photos: [], selectedIndex: 0 }
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const selectedFeatures = carProfile?.features || [];

  const issueItems = useMemo(() => 
    getIssueItems(itemStatuses, notes, selectedFeatures),
    [itemStatuses, notes, selectedFeatures]
  );

  // Separate critical/major issues from warnings (attention)
  const { criticalIssues, warningIssues } = useMemo(() => {
    const critical = [];
    const warnings = [];
    
    issueItems.forEach(item => {
      if (item.status === 'fail' || DELIVERY_BLOCKERS.includes(item.id)) {
        critical.push(item);
      } else {
        warnings.push(item);
      }
    });
    
    return { criticalIssues: critical, warningIssues: warnings };
  }, [issueItems]);

  // Free escape hatch: plain-text summary anyone can share
  const buildTextSummary = () => {
    const car = [carProfile?.carMake, carProfile?.carModel].filter(Boolean).join(' ') || 'My car';
    const lines = [
      `🚗 PDI Summary - ${car}`,
      `Score: ${pdiScore.scorePercentage}% (${pdiScore.checked}/${pdiScore.total} items checked)`,
      `✅ ${pdiScore.pass} passed · ❌ ${pdiScore.fail} failed · ⚠️ ${pdiScore.attention} need attention`,
    ];
    if (criticalIssues.length > 0) {
      lines.push('', 'Critical issues:');
      criticalIssues.slice(0, 8).forEach(item => lines.push(`• ${item.title}`));
      if (criticalIssues.length > 8) {
        lines.push(`…and ${criticalIssues.length - 8} more`);
      }
    }
    lines.push('', `Checklist: ${SITE_URL}`);
    return lines.join('\n');
  };

  const handleShareTextSummary = async () => {
    const text = buildTextSummary();
    track('free_summary_shared');
    if (navigator.share) {
      try {
        await navigator.share({ text });
      } catch {
        // user cancelled the share sheet
      }
    } else {
      await navigator.clipboard.writeText(text);
      showToast('Summary copied to clipboard');
    }
  };

  const openExport = (target) => {
    if (!unlocked) {
      setShowPaywall(target);
    } else if (target === 'report') {
      setShowPDFExport(true);
    } else if (target === 'share-link') {
      handleShareLink();
    } else {
      setShowPrintableChecklist(true);
    }
  };

  // Create a public, shareable web link for this report (premium — call via
  // openExport('share-link') so the paywall gate applies).
  const handleShareLink = async () => {
    if (shareUrl) {
      // already created — copy it
      await navigator.clipboard.writeText(window.location.origin + shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      return;
    }
    setSharing(true);
    try {
      const payload = await buildSharePayload({ pdiScore, carProfile, itemStatuses, notes, photos });
      const path = await saveSharedReport(payload);
      const fullUrl = window.location.origin + path;
      setShareUrl(path);
      track('report_link_created');
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'My Car PDI Report',
            text: 'Here is my pre-delivery inspection report:',
            url: fullUrl,
          });
        } catch {
          // share sheet cancelled — link is still shown for copying
        }
      }
    } catch (err) {
      showToast(err.message || 'Could not create share link');
    } finally {
      setSharing(false);
    }
  };

  const confirmReset = () => {
    clearAllData();
    navigate('/');
    setShowResetModal(false);
  };

  // Score color based on percentage
  const getScoreGradient = (score) => {
    if (score >= 90) return 'from-emerald-400 via-green-500 to-teal-500';
    if (score >= 70) return 'from-amber-400 via-orange-500 to-yellow-500';
    return 'from-rose-400 via-red-500 to-pink-500';
  };

  const getScoreEmoji = (score) => {
    if (score >= 90) return '🎉';
    if (score >= 70) return '👍';
    return '⚠️';
  };

  const IssueCard = ({ item, isCritical }) => {
    const isExpanded = expandedIssue === item.id;
    const itemPhotos = photos[item.id] || [];
    const itemNote = notes[item.id];
    const isResolved = resolvedItems[item.id];
    const hasExpandableContent = itemNote || itemPhotos.length > 0;

    return (
      <div 
        className={`rounded-xl border-2 overflow-hidden transition-all duration-300 ${
          isCritical 
            ? 'bg-gradient-to-br from-danger-50 to-rose-50 border-danger-200 hover:border-danger-300' 
            : 'bg-gradient-to-br from-accent-50 to-amber-50 border-accent-200 hover:border-accent-300'
        } ${isResolved ? 'opacity-60' : ''}`}
      >
        <div className="p-4 flex items-start justify-between gap-3">
          {/* Clickable area for expand/collapse */}
          <div 
            className={`flex items-start gap-3 flex-1 min-w-0 ${hasExpandableContent ? 'cursor-pointer' : ''}`}
            onClick={() => hasExpandableContent && setExpandedIssue(isExpanded ? null : item.id)}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isCritical ? 'bg-danger-100' : 'bg-accent-100'
            }`}>
              {isCritical ? (
                <XCircle className="w-4 h-4 text-danger-500" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-accent-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`font-semibold ${isCritical ? 'text-danger-800' : 'text-accent-800'}`}>
                {item.title}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">{item.sectionTitle}</p>
              
              {/* Indicators */}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {itemNote && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-full text-xs text-slate-600">
                    <MessageSquare className="w-3 h-3" />
                    Note
                  </span>
                )}
                {itemPhotos.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-full text-xs text-slate-600">
                    <Camera className="w-3 h-3" />
                    {itemPhotos.length} photo{itemPhotos.length > 1 ? 's' : ''}
                  </span>
                )}
                {isResolved && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-100 text-brand-700 rounded-full text-xs font-semibold">
                    <Wrench className="w-3 h-3" />
                    Fixed
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Action buttons - separate from clickable area */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {!isResolved && (
              <button
                onClick={() => markAsResolved(item.id, true)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                  isCritical 
                    ? 'text-danger-600 bg-danger-100 hover:bg-danger-200' 
                    : 'text-accent-600 bg-accent-100 hover:bg-accent-200'
                }`}
              >
                Mark Fixed
              </button>
            )}
            {hasExpandableContent && (
              <div 
                onClick={() => setExpandedIssue(isExpanded ? null : item.id)}
                className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer ${
                  isCritical ? 'bg-danger-100 hover:bg-danger-200' : 'bg-accent-100 hover:bg-accent-200'
                } transition-colors`}
              >
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-slate-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Expanded Content - Notes & Photos */}
        {isExpanded && (itemNote || itemPhotos.length > 0) && (
          <div className="px-4 pb-4 pt-0 border-t border-white/50 animate-slide-down">
            {itemNote && (
              <div className="mt-3 p-3 bg-white rounded-xl shadow-sm">
                <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> Note
                </p>
                <p className="text-sm text-slate-700">{itemNote}</p>
              </div>
            )}
            
            {itemPhotos.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
                  <Camera className="w-3 h-3" /> Photos (tap to enlarge)
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {itemPhotos.map((photo, index) => (
                    <div 
                      key={photo.id} 
                      className="aspect-square rounded-xl overflow-hidden bg-slate-100 shadow-sm relative group cursor-pointer"
                      onClick={() => setLightboxData({ photos: itemPhotos, selectedIndex: index })}
                    >
                      <img 
                        src={photo.data} 
                        alt="Issue evidence"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 animate-fade-in">
      {/* Header */}
      <div className="text-center mb-6">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-3 ${
          pdiScore.pending === 0 
            ? 'bg-brand-50 text-brand-700' 
            : 'bg-amber-50 text-amber-700'
        }`}>
          {pdiScore.pending === 0 ? (
            <>
              <Target className="w-3.5 h-3.5" />
              INSPECTION COMPLETE
            </>
          ) : (
            <>
              <Clock className="w-3.5 h-3.5" />
              {pdiScore.pending} ITEMS PENDING
            </>
          )}
        </div>
        <h1 className="font-display text-3xl font-bold text-slate-800">
          PDI Summary
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Review your inspection results
        </p>
      </div>

      {/* Score Card */}
      <div className={`relative overflow-hidden bg-gradient-to-br ${getScoreGradient(pdiScore.scorePercentage)} rounded-3xl p-6 text-white shadow-2xl mb-6`}>
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-5 h-5 text-white/80" />
              <p className="text-white/80 text-sm font-semibold">PDI Score</p>
            </div>
            <p className="text-6xl font-display font-bold">
              {pdiScore.scorePercentage}
              <span className="text-3xl">%</span>
            </p>
            <p className="text-white/70 text-sm mt-2">
              Based on {pdiScore.checked} items checked {getScoreEmoji(pdiScore.scorePercentage)}
            </p>
          </div>
          <div className="w-28 h-28 relative">
            <svg className="w-28 h-28 -rotate-90" viewBox="0 0 36 36">
              <path
                className="text-white/20"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="text-white score-ring"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                fill="none"
                strokeDasharray={`${pdiScore.scorePercentage}, 100`}
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <TrendingUp className="absolute inset-0 m-auto w-10 h-10 text-white" />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="stat-card bg-gradient-to-br from-white to-brand-50 rounded-2xl p-4 border-2 border-brand-100 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-green-500 flex items-center justify-center mb-3 shadow-lg">
            <CheckCircle2 className="w-5 h-5 text-white" />
          </div>
          <p className="text-3xl font-bold text-brand-600">{pdiScore.pass}</p>
          <p className="text-xs text-slate-500 font-semibold mt-1">PASSED</p>
        </div>

        <div className="stat-card bg-gradient-to-br from-white to-danger-50 rounded-2xl p-4 border-2 border-danger-100 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-danger-400 to-rose-500 flex items-center justify-center mb-3 shadow-lg">
            <XCircle className="w-5 h-5 text-white" />
          </div>
          <p className="text-3xl font-bold text-danger-600">{pdiScore.fail}</p>
          <p className="text-xs text-slate-500 font-semibold mt-1">FAILED</p>
        </div>

        <div className="stat-card bg-gradient-to-br from-white to-accent-50 rounded-2xl p-4 border-2 border-accent-100 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-400 to-orange-500 flex items-center justify-center mb-3 shadow-lg">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <p className="text-3xl font-bold text-accent-600">{pdiScore.attention}</p>
          <p className="text-xs text-slate-500 font-semibold mt-1">ATTENTION</p>
        </div>

        <button
          onClick={() => navigate('/checklist')}
          className="stat-card bg-gradient-to-br from-white to-slate-100 rounded-2xl p-4 border-2 border-slate-200 shadow-sm hover:shadow-lg hover:border-slate-400 hover:-translate-y-1 transition-all text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center mb-3 shadow-lg">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <p className="text-3xl font-bold text-slate-600">{pdiScore.pending}</p>
          <p className="text-xs text-slate-500 font-semibold mt-1">PENDING</p>
        </button>
      </div>

      {/* Critical/Major Issues Section */}
      {criticalIssues.length > 0 && (
        <div className="relative overflow-hidden bg-gradient-to-br from-danger-50 to-rose-100 border-2 border-danger-200 rounded-3xl p-5 mb-6">
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-danger-200/40 rounded-full blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-danger-500 to-rose-500 flex items-center justify-center shadow-lg">
                <AlertOctagon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-danger-800 text-xl">
                  Critical Issues
                </h3>
                <p className="text-xs text-danger-600 font-medium">{criticalIssues.length} item{criticalIssues.length > 1 ? 's' : ''} must be resolved</p>
              </div>
            </div>
            
            <div className="space-y-3">
              {criticalIssues.map(item => (
                <IssueCard key={item.id} item={item} isCritical />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Warning Issues Section */}
      {warningIssues.length > 0 && (
        <div className="relative overflow-hidden bg-gradient-to-br from-accent-50 to-amber-100 border-2 border-accent-200 rounded-3xl p-5 mb-6">
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-accent-200/40 rounded-full blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-accent-500 to-orange-500 flex items-center justify-center shadow-lg">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-accent-800 text-xl">
                  Warnings
                </h3>
                <p className="text-xs text-accent-600 font-medium">{warningIssues.length} item{warningIssues.length > 1 ? 's' : ''} to review</p>
              </div>
            </div>
            
            <div className="space-y-3">
              {warningIssues.map(item => (
                <IssueCard key={item.id} item={item} isCritical={false} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* What To Do Section */}
      {issueItems.length > 0 && (
        <div className="relative overflow-hidden bg-slate-900 rounded-3xl p-6 mb-6 text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 via-transparent to-teal-500/20" />
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-brand-500/20 rounded-full blur-3xl" />
          
          <div className="relative">
            <h3 className="font-bold text-xl mb-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-teal-400 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-white" />
              </div>
              What To Do Next
            </h3>
            <ul className="space-y-3 text-slate-300 text-sm">
              {[
                'Get written acknowledgment from the dealer for all failed items',
                'Request issues to be fixed before registration if possible',
                'For critical failures, consider rejecting delivery until fixed',
                'Keep this report and photos as documentation',
              ].map((text, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0 text-xs font-bold text-brand-400 border border-slate-700">
                    {i + 1}
                  </div>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Section Progress */}
      <div className="bg-white rounded-3xl border-2 border-slate-200 shadow-sm mb-6 overflow-hidden">
        <div className="p-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-brand-500" />
            Section Progress
          </h3>
        </div>
        <div className="divide-y divide-slate-100">
          {CHECKLIST_SECTIONS.map((section, i) => {
            const progress = sectionProgress[section.id];
            return (
              <div 
                key={section.id}
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors group"
                onClick={() => navigate('/checklist')}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-700 truncate group-hover:text-brand-600 transition-colors">
                    {section.title}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-brand-400 to-brand-500 rounded-full transition-all duration-500"
                        style={{ width: `${progress.percentage}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-slate-500 w-10">
                      {progress.percentage}%
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300 ml-3 group-hover:text-brand-500 group-hover:translate-x-1 transition-all" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Export Actions */}
      <div className="bg-gradient-to-br from-brand-50 to-teal-50 rounded-3xl border-2 border-brand-100 shadow-sm p-5 mb-6">
        <h3 className="font-bold text-slate-800 text-lg mb-4 flex items-center gap-2">
          <Share2 className="w-5 h-5 text-brand-500" />
          Export & Share
        </h3>
        <div className="space-y-3">
          <button
            onClick={() => openExport('report')}
            className="w-full p-4 gradient-animated rounded-2xl flex items-center justify-center gap-3 btn-touch shadow-lg hover:shadow-xl transition-shadow text-white font-bold"
          >
            {unlocked ? <FileDown className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
            <span>Download PDF Report</span>
            {!unlocked && (
              <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-bold">
                ₹{UNLOCK_PRICE_RUPEES}
              </span>
            )}
          </button>

          {/* Printable Offline Checklist */}
          <button
            onClick={() => openExport('printable')}
            className="w-full p-4 bg-white border-2 border-slate-200 rounded-2xl flex items-center justify-between gap-3 btn-touch hover:border-brand-300 hover:bg-brand-50/50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center group-hover:from-brand-100 group-hover:to-brand-200 transition-colors">
                <Printer className="w-5 h-5 text-slate-600 group-hover:text-brand-600 transition-colors" />
              </div>
              <div className="text-left">
                <p className="font-bold text-slate-800 group-hover:text-brand-700 transition-colors flex items-center gap-2">
                  Printable Checklist
                  {!unlocked && <Lock className="w-3.5 h-3.5 text-slate-400" />}
                </p>
                <p className="text-xs text-slate-500">
                  Download & print for offline use
                </p>
              </div>
            </div>
            <ClipboardList className="w-5 h-5 text-slate-400 group-hover:text-brand-500 transition-colors" />
          </button>

          {/* Free text summary */}
          <button
            onClick={handleShareTextSummary}
            className="w-full p-4 bg-white border-2 border-slate-200 rounded-2xl flex items-center justify-between gap-3 btn-touch hover:border-brand-300 hover:bg-brand-50/50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center group-hover:from-brand-100 group-hover:to-brand-200 transition-colors">
                <MessageSquare className="w-5 h-5 text-slate-600 group-hover:text-brand-600 transition-colors" />
              </div>
              <div className="text-left">
                <p className="font-bold text-slate-800 group-hover:text-brand-700 transition-colors">
                  Text Summary
                </p>
                <p className="text-xs text-slate-500">
                  Share via WhatsApp or copy - always free
                </p>
              </div>
            </div>
            <span className="px-2 py-0.5 bg-brand-100 text-brand-700 rounded-full text-xs font-bold">
              FREE
            </span>
          </button>

          {/* Shareable web link (premium) */}
          <button
            onClick={() => openExport('share-link')}
            disabled={sharing}
            className="w-full p-4 bg-white border-2 border-slate-200 rounded-2xl flex items-center justify-between gap-3 btn-touch hover:border-brand-300 hover:bg-brand-50/50 transition-all group disabled:opacity-70"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center group-hover:from-brand-100 group-hover:to-brand-200 transition-colors">
                {sharing ? (
                  <Loader2 className="w-5 h-5 text-brand-600 animate-spin" />
                ) : (
                  <Link2 className="w-5 h-5 text-slate-600 group-hover:text-brand-600 transition-colors" />
                )}
              </div>
              <div className="text-left">
                <p className="font-bold text-slate-800 group-hover:text-brand-700 transition-colors flex items-center gap-2">
                  {shareUrl ? 'Copy share link' : 'Get a shareable link'}
                  {!unlocked && <Lock className="w-3.5 h-3.5 text-slate-400" />}
                </p>
                <p className="text-xs text-slate-500">
                  {sharing ? 'Creating link...' : 'A web page anyone can open — great for WhatsApp'}
                </p>
              </div>
            </div>
            <Share2 className="w-5 h-5 text-slate-400 group-hover:text-brand-500 transition-colors" />
          </button>

          {/* Created link display */}
          {shareUrl && (
            <div className="p-3 rounded-2xl bg-brand-50 border-2 border-brand-200 flex items-center gap-2">
              <p className="flex-1 min-w-0 text-sm text-brand-800 font-medium truncate">
                {window.location.origin + shareUrl}
              </p>
              <button
                onClick={handleShareLink}
                className="w-9 h-9 rounded-lg bg-white flex items-center justify-center text-brand-600 flex-shrink-0"
                aria-label="Copy link"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Clear Data Action */}
      <div className="space-y-3">
        <button
          onClick={() => setShowResetModal(true)}
          className="w-full bg-gradient-to-br from-danger-50 to-rose-100 border-2 border-danger-200 text-danger-700 font-bold py-4 px-6 rounded-2xl btn-touch flex items-center justify-center gap-2 hover:border-danger-300 transition-colors"
        >
          <Trash2 className="w-5 h-5" />
          Start New Inspection
        </button>
        <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1">
          <Sparkles className="w-3 h-3" />
          This will clear all data and start fresh for a new car
        </p>

        <Link
          to="/legal"
          className="flex items-center justify-center gap-2 text-sm font-semibold text-slate-500 hover:text-brand-600 transition-colors py-2"
        >
          <Scale className="w-4 h-4" />
          Know your legal rights (Consumer Protection Act)
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Reset Modal */}
      {showResetModal && (
        <ResetConfirmModal
          type="all"
          onConfirm={confirmReset}
          onCancel={() => setShowResetModal(false)}
        />
      )}

      {/* PDF Export Modal */}
      {showPDFExport && (
        <PDFExport onClose={() => setShowPDFExport(false)} />
      )}

      {/* Printable Checklist Modal */}
      {showPrintableChecklist && (
        <PrintableChecklist onClose={() => setShowPrintableChecklist(false)} />
      )}

      {/* Paywall */}
      {showPaywall && (
        <PaywallModal
          feature={showPaywall}
          onClose={() => setShowPaywall(null)}
          onUnlocked={() => {
            const target = showPaywall;
            setShowPaywall(null);
            if (target === 'report') {
              setShowPDFExport(true);
            } else if (target === 'share-link') {
              handleShareLink();
            } else {
              setShowPrintableChecklist(true);
            }
          }}
        />
      )}

      {/* Photo Lightbox */}
      {lightboxData && (
        <PhotoLightbox
          photos={lightboxData.photos}
          selectedIndex={lightboxData.selectedIndex}
          onClose={() => setLightboxData(null)}
          onNavigate={(index) => setLightboxData({ ...lightboxData, selectedIndex: index })}
        />
      )}
    </div>
  );
};

export default SummaryPage;
